import { Collection, LongString, MaxString, ModelBase, NormalString, ObservableCollection, Property, Required, VeryLongString } from '@skylab-shared/core';
import { AddressModel } from '../address/address.model';
import { CalculationMethod } from '../common/calculation-method';
import { RelatedEntity } from '../common/related-entity';
import { VatReleaseReasonSource } from '../company/vat-release-reason-source';
import { DocumentBuyerModel } from '../document/document-buyer.model';
import { DocumentClientModel } from '../document/document-client.model';
import { DocumentPaymentListItemModel } from '../document/document-payment-list-item.model';
import { ExchangeRateModel } from '../exchange-rate/exchange-rate.model';
import { InventoryEffect } from '../inventory/inventory-effect';
import { VatTableItemModel } from '../vat-table/vat-table-item.model';
import { SaleDocumentItemModel } from './sale-document-item.model';
import { SaleDocumentKind } from './sale-document-kind';
import { SplitPaymentState } from './split-payment-state';

export class SaleProFormaModel extends ModelBase {
    @Property({ identifiable: true })
    public id: number;

    @Property(Number, { nullable: true })
    public bankAccountNumberId?: number | null;

    @Property(Number, { nullable: true })
    public baseCurrencyBankAccountNumberId?: number | null;

    @Property(DocumentBuyerModel, { nullable: true })
    public buyer: DocumentBuyerModel | null;

    @Property()
    @Required()
    public calculationMethod: CalculationMethod;

    @Property()
    public cashBasis: boolean;

    @Property(DocumentClientModel, {nullable: true})
    public client: DocumentClientModel | null;

    @Property()
    @Required()
    public communityTransactionId: number;

    @Property()
    @Required()
    public currencyId: number;

    @Property()
    public customFlags: number;

    @Property()
    public deliveryAddress: AddressModel;

    @Property()
    public dueDate: Date;

    @Property()
    public dueDateInDays: number;

    @Property()
    public exchangeRate: ExchangeRateModel;

    @Property()
    public favourite: boolean;

    @Property()
    public grossValue: number;

    @Property()
    public issueDate: Date;

    @Property()
    public issuedByVatPayer: boolean;

    @Property()
    public issuer: string;

    @Collection({ type: SaleDocumentItemModel })
    public items: ObservableCollection<SaleDocumentItemModel>;

    @Property()
    public jpkVatGroups: string[];

    @Property()
    @Required()
    public kind: SaleDocumentKind;

    @Property()
    public myCompanyId: number;

    @Property()
    public netValue: number;

    @Property()
    @MaxString('data-model.document.validation.note.length')
    public note: string;

    @Property()
    public numberingId: number;

    @Property()
    @VeryLongString('data-model.document.validation.document-number.length')
    public documentNumber: string;

    @Property(Date,{ nullable: true })
    public orderDate?: Date | null;

    @Collection({ type: DocumentPaymentListItemModel })
    public payments: ObservableCollection<DocumentPaymentListItemModel>;

    @Property()
    public paymentMethodId: number;

    @Property()
    @NormalString('data-model.document.validation.recipient.length')
    public recipient: string;

    @Property()
    public splitPayment: SplitPaymentState;

    @Property()
    @Required('data-model.document.validation.transaction-location.non-empty')
    @LongString('data-model.document.validation.transaction-location.length')
    public transactionLocation: string;

    @Collection({ type: VatTableItemModel })
    public vatTableItems: ObservableCollection<VatTableItemModel>;

    @Collection({ type: VatTableItemModel })
    public vatTableItemsInBaseCurrency: ObservableCollection<VatTableItemModel>;

    @Property({ nullable: true })
    public vatReleaseReasonId?: number;

    @Property({ nullable: true })
    public vatReleaseReasonCustomName?: string;

    @Property({ nullable: true })
    public vatReleaseReasonSource?: VatReleaseReasonSource;

    @Property({ nullable: true })
    public wstoShipmentCountryId: number;

    @Property(Number, { nullable: true })
    public wstoConsumerCountryId: number | null;

    @Property({ nullable: true })
    @MaxString('data-model.document.validation.wsto-additional-information.length')
    public wstoAdditionalInformation: string;

    @Property()
    @MaxString('data-model.document.validation.custom-due-date-description.length')
    public customDueDateDescription: string;

    @Property()
    public relatedInventoryDocuments: RelatedEntity[];

    @Property()
    public includesInventoryControl: boolean;

    @Property(Number, {nullable: true})
    public readonly inventoryEffect: InventoryEffect | null = null;
}
